// usePagiflow.ts

import {
  inject,
  Signal,
  signal,
  effect,
  untracked,
  type OnInit,
  type OnDestroy,
  Injector,
} from '@angular/core';

import type { PagiflowInstance, PagiflowOptions } from './types';

type PagiflowCtor = (
  selector: string | HTMLElement,
  opts?: PagiflowOptions
) => PagiflowInstance;

function getPagiflow(): PagiflowCtor | null {
  if (typeof window !== 'undefined' && (window as any).Pagiflow) {
    return (window as any).Pagiflow as PagiflowCtor;
  }
  try {
    const mod = require('pagiflow');
    return (mod.default ?? mod) as PagiflowCtor;
  } catch {
    return null;
  }
}

function initPagiflowFromElement(
  ctor: PagiflowCtor,
  el: HTMLElement,
  opts: PagiflowOptions
): PagiflowInstance | null {
  const attrValue = `pf-${Math.random().toString(36).slice(2)}`;
  const selector = `[data-pagiflow-angular-id="${attrValue}"]`;
  el.setAttribute('data-pagiflow-angular-id', attrValue);

  const bySelector = ctor(selector, opts) as PagiflowInstance | null;
  if (!bySelector) {
    el.removeAttribute('data-pagiflow-angular-id');
    return null;
  }
  return bySelector;
}

export interface UsePagiflowReturn {
  /** Signal for the slider ref */
  sliderRef: Signal<HTMLElement | null>;
  /** Signal for Pagiflow instance */
  instance: Signal<PagiflowInstance | null>;
  /** Signal for current slide index */
  currentIndex: Signal<number>;
  /** Navigate to a specific slide */
  goTo: (index: number, opts?: { silent?: boolean; instant?: boolean }) => void;
  /** Go to next slide */
  next: () => void;
  /** Go to previous slide */
  prev: () => void;
  /** Start autoScroll */
  play: () => void;
  /** Pause autoScroll */
  pause: () => void;
  /** Toggle play/pause */
  togglePlayPause: () => void;
  /** Update options */
  setOptions: (opts: Partial<PagiflowOptions>, rebuild?: boolean) => void;
  /** Reinitialize with new options */
  reInit: (opts?: Partial<PagiflowOptions>) => void;
}

/**
 * Angular composable (using signals) for Pagiflow slider integration.
 * Usage:
 *   export class SliderComponent {
 *     pagi = usePagiflow({ direction: 'horizontal' });
 *
 *     onMount() {
 *       this.pagi.sliderRef.set(this.elementRef.nativeElement);
 *     }
 *   }
 */
export function usePagiflow(options: PagiflowOptions = {}): UsePagiflowReturn {
  const injector = inject(Injector);
  const sliderRef = signal<HTMLElement | null>(null);
  const instance = signal<PagiflowInstance | null>(null);
  const currentIndex = signal<number>(options.startIndex ?? 0);

  let resizeObserver: ResizeObserver | null = null;
  let raf1 = 0;
  let raf2 = 0;
  let timeoutId = 0;

  // Setup effect to initialize Pagiflow when sliderRef changes
  effect(() => {
    const el = sliderRef();
    if (!el) return;

    const existing = (el as any).pagiflowInstance as PagiflowInstance | undefined;
    if (existing && !existing.destroyed) {
      instance.set(existing);
      return;
    }

    const ctor = getPagiflow();
    if (!ctor) {
      console.error('[usePagiflow] Pagiflow core not found. Install pagiflow or load it via CDN.');
      return;
    }

    const inst = initPagiflowFromElement(ctor, el, options);
    if (!inst) return;

    instance.set(inst);
    (el as any).pagiflowInstance = inst;

    // Setup resize observer
    resizeObserver = new ResizeObserver(() => {
      cancelAnimationFrame(raf1);
      raf1 = requestAnimationFrame(() => {
        cancelAnimationFrame(raf2);
        raf2 = requestAnimationFrame(() => {
          inst.reInit();
        });
      });
    });
    resizeObserver.observe(el);

    // Listen to slide changes via instance API
    inst.onSlideChange((idx) => {
      currentIndex.set(idx);
    });

    return () => {
      cleanup();
      if (inst && !inst.destroyed) inst.destroy();
      instance.set(null);
    };
  });

  const cleanup = () => {
    if (resizeObserver) {
      resizeObserver.disconnect();
      resizeObserver = null;
    }
    cancelAnimationFrame(raf1);
    cancelAnimationFrame(raf2);
    clearTimeout(timeoutId);
  };

  const goTo = (index: number, opts?: { silent?: boolean; instant?: boolean }) => {
    const inst = untracked(() => instance());
    inst?.goTo(index, opts);
  };

  const next = () => {
    const inst = untracked(() => instance());
    inst?.next();
  };

  const prev = () => {
    const inst = untracked(() => instance());
    inst?.prev();
  };

  const play = () => {
    const inst = untracked(() => instance());
    inst?.play();
  };

  const pause = () => {
    const inst = untracked(() => instance());
    inst?.pause();
  };

  const togglePlayPause = () => {
    const inst = untracked(() => instance());
    inst?.togglePlayPause();
  };

  const setOptions = (opts: Partial<PagiflowOptions>, rebuild?: boolean) => {
    const inst = untracked(() => instance());
    inst?.setOptions(opts, rebuild);
  };

  const reInit = (opts?: Partial<PagiflowOptions>) => {
    const inst = untracked(() => instance());
    inst?.reInit(opts);
  };

  return {
    sliderRef,
    instance,
    currentIndex,
    goTo,
    next,
    prev,
    play,
    pause,
    togglePlayPause,
    setOptions,
    reInit,
  };
}
